/**
 * chatAttachments.js — pure helpers for the chat file-attachment flow.
 *
 * All functions here are side-effect free (no state, no DOM) so they can be
 * imported by any component that needs to reason about an uploaded file.
 * Component-level orchestration (onPick / drag-drop UI / attachment list
 * state) still lives inside chat.jsx.
 */

export const IMG_MIMES = ["image/jpeg", "image/png", "image/webp"];

export const DOC_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",       // .xlsx
  "application/vnd.ms-excel",                                                 // .xls (legacy)
  "text/csv",
  "application/csv",
  "text/plain",
  "text/markdown",
];

// Comma-joined `accept=""` attribute value for <input type="file">.
export const ACCEPT_ATTR = [
  ...IMG_MIMES,
  ...DOC_MIMES,
  ".pdf",
  ".docx",
  ".xlsx",
  ".csv",
  ".txt",
  ".md",
].join(",");

export const MAX_FILES = 5;
export const MAX_IMG_BYTES = 5 * 1024 * 1024;
export const MAX_DOC_BYTES = 15 * 1024 * 1024;

/** Guess mime for CSVs / txt pasted with no type (some browsers set ""). */
export function guessMime(file) {
  if (file.type) return file.type;
  const n = (file.name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "application/pdf";
  if (n.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (n.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (n.endsWith(".csv"))  return "text/csv";
  if (n.endsWith(".md") || n.endsWith(".markdown")) return "text/markdown";
  if (n.endsWith(".txt")) return "text/plain";
  return "";
}

/** Coarse category for routing an attachment through the chat pipeline. */
export function kindFor(mime) {
  if (IMG_MIMES.includes(mime)) return "image";
  if (DOC_MIMES.includes(mime)) return "doc";
  return null;
}

/** Short label ("PDF", "DOCX", …) shown on doc chips. */
export function docKind(mime) {
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("wordprocessingml")) return "DOCX";
  if (mime.includes("spreadsheetml") || mime === "application/vnd.ms-excel") return "XLSX";
  if (mime === "text/csv" || mime === "application/csv") return "CSV";
  if (mime === "text/markdown") return "MD";
  return "TXT";
}
